import { createSelector } from 'reselect'
import moment from 'moment'
import * as ActivityDetailLevel from 'const/ActivityDetailLevel'
import { convertToGroup } from 'utils/activities'
import mapActivity from 'utils/mapActivity'
import { currentUserSelector } from 'selectors/currentUser'

export const getActivities = (state, props) => {
  return props.activities
}

export const getVisitors = (state, props) => {
  return props.visitors
}

export const getDetailLevel = (state, props) => {
  return props.detailLevel
}

export const getContext = (state, props) => {
  return props.context
}

const emptyArray = []

export const makeActivityGroupsSelector = () => createSelector(
  [
    getActivities,
    getVisitors,
    getDetailLevel,
    getContext,
    currentUserSelector
  ],
  (
    activities,
    visitors,
    detailLevel,
    context,
    currentUser
  ) => {
    const visitorsToDisplay =
      visitors
        ? [...visitors]
        : emptyArray

    return activities
      .map(activity => mapActivity(activity, currentUser, context))
      .sort((a, b) => b.timestamp - a.timestamp)
      .reduce((acc, activity) => {
        const accLength = acc.length

        let activityVisitors = visitorsToDisplay

        if (visitorsToDisplay.length) {
          activityVisitors = []

          let i = visitorsToDisplay.length
          while (i--) {
            const visitor = visitorsToDisplay[i]

            if (
              (visitor.action && visitor.action === 'Being') ||
              (visitor.timestamp > activity.timestamp)
            ) {
              activityVisitors.push(visitor)

              visitorsToDisplay.splice(i, 1)
            }
          }
        }

        let type = 'activityGroup'

        if (
          (
            detailLevel === ActivityDetailLevel.COMMENTS &&
            activity.tags &&
            activity.tags.indexOf('message') === -1
          ) || (
            detailLevel === ActivityDetailLevel.IMPORTANT &&
            activity.tags &&
            activity.tags.indexOf('important') === -1
          )
        ) {
          type = 'hidden'
        }

        if (accLength > 0) {
          const lastGroup = acc[accLength - 1]

          if (
            !activityVisitors.length && // Split groups on visitors
            type === lastGroup.type && // Group only same type
            (
              type === 'hidden'
                ? true
                : ( // Group only on common data
                  lastGroup.from === activity.from &&
                  lastGroup.boardId === activity.data.boardId &&
                  lastGroup.cardId === activity.data.cardId &&
                  moment(lastGroup.timestamp).diff(activity.timestamp, 'minutes') <= 5
                )
            )
          ) {
            lastGroup.boardName = activity.data.boardName
            lastGroup.cardName = activity.data.cardName
            lastGroup.activities.push(activity)
            lastGroup.key = activity.id

            return acc
          }
        }

        const group = convertToGroup(activity, type)

        group.visitors = activityVisitors.sort((a, b) => {
          const isAOnline = a.action && a.action === 'Being'
          const isBOnline = b.action && b.action === 'Being'

          if (isAOnline && isBOnline) return a.id - b.id
          if (isAOnline && !isBOnline) return 1
          if (!isAOnline && isBOnline) return -1

          return a.timestamp - b.timestamp
        })

        acc.push(group)

        return acc
      }, [])
  }
)

export const makeTimeGroupsSelector = () => {
  const activityGroupsSelector = makeActivityGroupsSelector()

  return createSelector(
    [
      activityGroupsSelector
    ],
    (
      activityGroups
    ) =>
      activityGroups
        .reduce((acc, activityGroup) => {
          const length = acc.length

          for (let i = 0; i < length; i++) {
            if (acc[i].test(activityGroup)) {
              acc[i].activityGroups.push(activityGroup)
              if (!acc[i].timestamp) acc[i].timestamp = activityGroup.timestamp

              break
            }
          }

          return acc
        }, [
          {
            formattedMessageId: 'TIME_GROUP_TODAY',
            test: activityGroup => moment(activityGroup.timestamp).isSame(moment(), 'day'),
            activityGroups: []
          },
          {
            formattedMessageId: 'TIME_GROUP_YESTERDAY',
            test: activityGroup => moment(activityGroup.timestamp).isSame(moment().subtract(1, 'day'), 'day'),
            activityGroups: []
          },
          {
            formattedMessageId: 'TIME_GROUP_THIS_WEEK',
            test: activityGroup => moment(activityGroup.timestamp).isSameOrAfter(moment(), 'week'),
            activityGroups: []
          },
          {
            formattedMessageId: 'TIME_GROUP_LAST_WEEK',
            test: activityGroup => moment(activityGroup.timestamp).isSameOrAfter(moment().subtract(1, 'week'), 'week'),
            activityGroups: []
          },
          {
            formattedMessageId: 'TIME_GROUP_2_WEEKS_AGO',
            test: activityGroup => moment(activityGroup.timestamp).isSameOrAfter(moment().subtract(2, 'week'), 'week'),
            activityGroups: []
          },
          {
            formattedMessageId: 'TIME_GROUP_THIS_MONTH',
            test: activityGroup => moment(activityGroup.timestamp).isSameOrAfter(moment(), 'month'),
            activityGroups: []
          },
          {
            formattedMessageId: 'TIME_GROUP_LAST_MONTH',
            test: activityGroup => moment(activityGroup.timestamp).isSameOrAfter(moment().subtract(1, 'month'), 'month'),
            activityGroups: []
          },
          {
            formattedMessageId: 'TIME_GROUP_OLDER',
            test: () => true,
            activityGroups: []
          },
        ])
        .reduce((timeGroups, timeGroup) => {
          if (timeGroup.activityGroups.length > 0) {
            timeGroups.push({
              formattedMessageId: timeGroup.formattedMessageId,
              activityGroups: timeGroup.activityGroups
            })
          }

          return timeGroups
        }, [])
  )
}

export default () => {
  const timeGroupsSelector = makeTimeGroupsSelector()

  return createSelector(
    [
      timeGroupsSelector
    ],
    (
      timeGroups
    ) => ({
      timeGroups
    })
  )
}
